import { Plus } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useCanManageParts } from 'corporate/hooks';
import { ICorporatePart, ICorporatePartPriceListItem } from 'corporate/types';
import { Color } from 'theme/enums';

import { UpsertCorporatePartPrice } from './UpsertCorporatePartPrice';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  price?: ICorporatePartPriceListItem;
  buttonElement?: ReactElement;
  onSubmitSuccess?: () => void;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
}

export const UpsertCorporatePartPriceButton = ({
  corporatePart,
  price,
  buttonElement,
  onSubmitSuccess,
  onRemoveSuccess,
  disabled = false,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();
  const canManageParts = useCanManageParts();

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsOpen(true),
      });
    }

    return (
      <Button
        text={t('buttonLabelAddPartPrice')}
        color={Color.SUCCESS}
        startDecorator={<Plus />}
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, disabled, t]);

  if (!canManageParts) {
    return null;
  }

  return (
    <>
      {button}

      {isOpen && (
        <UpsertCorporatePartPrice
          corporatePart={corporatePart}
          price={price}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSubmitSuccess={onSubmitSuccess}
          onRemoveSuccess={onRemoveSuccess}
        />
      )}
    </>
  );
};
